import { GenericResponse } from '../types';
import { getHeaders, tosApi } from './tosApi';
import { getInvalidatesTags } from './utils';

export type CargoSuppressionReportItemType = {
  docking_code: string;
  docking_year: number;
  docking_berth_code: string;
  docking_place_name: string | null;
  vessel_name: string | null;
  vessel_flag: string | null;
  last_cable_untied_in: string | null;
  last_date_xml: string | null;
};

export type CargoSuppressionReportParamsType = {
  code_or_vessel__name?: string;
  initial_date_report?: string | null;
  final_date_report?: string | null;
  page?: number;
  page_size?: number;
};

export type CargoSuppressionXmlExportParamsType = {
  list_of_atracacoes: Array<{
    docking_code: string;
    docking_year: number;
  }>;
};

export type CargoSuppressionItemType = {
  id: number;
  docking_code: string;
  docking_berth_code: string;
  local_atracacao: string;
  docking_year: number;
  reason: string;
  ncmsh2: string;
  ncmsh4: string | null;
  id_conteiner: string | null;
  full_or_empty: 'C' | 'V' | null;
  weight: string | null;
  created_at: string;
};

export type CargoSuppressionPrefillProductOption = {
  product_id: number;
  cargo_type: string | null;
  product_group_id: number | null;
  is_container: boolean;
  cargo_id: number | null;
  cargo_name: string | null;
  ncm_raw: string | null;
  ncmsh2: string | null;
  ncmsh4: string | null;
};

export type CargoSuppressionPrefillContainerOption = {
  code: string;
  is_full: boolean | null;
};

export type CargoSuppressionPrefillType = {
  docking_code: string | null;
  docking_year: number | null;
  docking_berth_code: string | null;
  local_atracacao: string | null;
  products: CargoSuppressionPrefillProductOption[];
  container_options: CargoSuppressionPrefillContainerOption[];
  has_container_operation: boolean;
  has_general_cargo_operation: boolean;
  preconditions_errors: string[];
  general_cargo_preconditions_errors: string[];
};

export type CargoSuppressionInternalCreatePayload = {
  type: 'CONTAINER' | 'GENERAL_CARGO';
  reason: string;
  id_conteiner?: string | null;
  full_or_empty?: 'C' | 'V' | null;
  product_id?: number | null;
  weight?: string | number | null;
};

export type CargoSuppressionInternalPatchPayload = {
  reason?: string;
  full_or_empty?: 'C' | 'V' | null;
  weight?: string | number | null;
};

export const cargoSuppressionApi = tosApi.injectEndpoints({
  endpoints: (build) => ({
    getCargoSuppressionReport: build.query<
      GenericResponse & { results: CargoSuppressionReportItemType[] },
      CargoSuppressionReportParamsType | void
    >({
      query: (params) => ({
        url: '/maritime/cargo-suppression/report',
        method: 'GET',
        headers: getHeaders,
        params: params || {},
      }),
      providesTags: () => [{ type: 'CargoSuppression', code: 'report' }],
    }),
    exportCargoSuppressionXml: build.mutation<
      void,
      CargoSuppressionXmlExportParamsType
    >({
      query: (body) => ({
        url: '/maritime/cargo-suppression/export-xml',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, error) =>
        getInvalidatesTags(error, [
          { type: 'CargoSuppression', code: 'report' },
        ]),
    }),
    getCargoSuppressionPrefill: build.query<
      CargoSuppressionPrefillType,
      { docking_code: string }
    >({
      query: ({ docking_code }) => ({
        url: `/maritime/docking/${encodeURIComponent(
          docking_code
        )}/cargo-suppression/prefill`,
        method: 'GET',
        headers: getHeaders,
      }),
      providesTags: (_r, _e, { docking_code }) => [
        { type: 'CargoSuppression', code: `prefill:${docking_code}` },
      ],
    }),
    getCargoSuppressionListByDocking: build.query<
      { docking_code: string; results: CargoSuppressionItemType[] },
      { docking_code: string }
    >({
      query: ({ docking_code }) => ({
        url: `/maritime/docking/${encodeURIComponent(
          docking_code
        )}/cargo-suppression`,
        method: 'GET',
        headers: getHeaders,
      }),
      providesTags: (_r, _e, { docking_code }) => [
        { type: 'CargoSuppression', code: `docking:${docking_code}` },
      ],
    }),
    createCargoSuppressionInternal: build.mutation<
      CargoSuppressionItemType,
      { docking_code: string; payload: CargoSuppressionInternalCreatePayload }
    >({
      query: ({ docking_code, payload }) => ({
        url: `/maritime/docking/${encodeURIComponent(
          docking_code
        )}/cargo-suppression`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, error, { docking_code }) =>
        getInvalidatesTags(error, [
          { type: 'CargoSuppression', code: `docking:${docking_code}` },
          { type: 'CargoSuppression', code: 'report' },
        ]),
    }),
    updateCargoSuppressionInternal: build.mutation<
      CargoSuppressionItemType,
      {
        docking_code: string;
        suppression_id: number;
        payload: CargoSuppressionInternalPatchPayload;
      }
    >({
      query: ({ docking_code, suppression_id, payload }) => ({
        url: `/maritime/docking/${encodeURIComponent(
          docking_code
        )}/cargo-suppression/${suppression_id}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, error, { docking_code }) =>
        getInvalidatesTags(error, [
          { type: 'CargoSuppression', code: `docking:${docking_code}` },
          { type: 'CargoSuppression', code: 'report' },
        ]),
    }),
    deleteCargoSuppressionInternal: build.mutation<
      { deleted_count: number; id: number },
      { docking_code: string; suppression_id: number }
    >({
      query: ({ docking_code, suppression_id }) => ({
        url: `/maritime/docking/${encodeURIComponent(
          docking_code
        )}/cargo-suppression/${suppression_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error, { docking_code }) =>
        getInvalidatesTags(error, [
          { type: 'CargoSuppression', code: `docking:${docking_code}` },
          { type: 'CargoSuppression', code: 'report' },
        ]),
    }),
    getOperationCargoSuppressionTotals: build.query<
      {
        operation_id: number;
        cargo_type: string | null;
        total_suppressed: number;
        total_suppressed_containers: number;
      },
      { operation_id: number; cargo_type?: string }
    >({
      query: ({ operation_id, cargo_type }) => ({
        url: `/operational/operations/${operation_id}/cargo-suppression-totals`,
        method: 'GET',
        headers: getHeaders,
        params: cargo_type ? { cargo_type } : undefined,
      }),
      providesTags: (_r, _e, { operation_id, cargo_type }) => [
        {
          type: 'CargoSuppression',
          code: `operation:${operation_id}:${cargo_type || 'all'}`,
        },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCargoSuppressionReportQuery,
  useExportCargoSuppressionXmlMutation,
  useGetCargoSuppressionPrefillQuery,
  useGetCargoSuppressionListByDockingQuery,
  useCreateCargoSuppressionInternalMutation,
  useUpdateCargoSuppressionInternalMutation,
  useDeleteCargoSuppressionInternalMutation,
  useGetOperationCargoSuppressionTotalsQuery,
} = cargoSuppressionApi;
